import { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { meetingsApi, type ItemKind, type MeetingItemCreateRequest } from '@/api/meetings';
import type { Envelope } from '@/api/envelopes';
import { parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import type { ItemAddress } from './itemAddress';
import { SubmitButton } from '@/components/SubmitButton';
import { denseFieldSx } from '@/components/denseField';
import { resolveTokens } from '@/theme/theme';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const KINDS: ItemKind[] = ['DECISION', 'COMMITMENT', 'GOAL', 'QUESTION'];

interface PersonOption {
  userId: number;
  displayName: string;
}

interface ItemCreateDialogProps {
  address: ItemAddress;
  people: PersonOption[];
  envelopes: Envelope[];
  currency: string | null;
  onClose: () => void;
  onSaved: () => void;
  /** The kind the dialog opens on (a topic is a QUESTION, TMMAPP-0152 §B 15); COMMITMENT by default. */
  initialKind?: ItemKind;
  /** The dialog's title; "New item" by default. */
  title?: string;
}

export function ItemCreateDialog({
  address,
  people,
  envelopes,
  currency,
  onClose,
  onSaved,
  initialKind = 'COMMITMENT',
  title,
}: ItemCreateDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const dense = denseFieldSx(tokens);
  const [kind, setKind] = useState<ItemKind>(initialKind);
  const [body, setBody] = useState('');
  const [ownerUserId, setOwnerUserId] = useState<number | ''>('');
  const [dueOn, setDueOn] = useState('');
  const [envelopeId, setEnvelopeId] = useState<number | ''>('');
  const [target, setTarget] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { dirty } = useDirty({ kind, body, ownerUserId, dueOn, envelopeId, target });
  // A standalone meeting's goal is words only: no envelope, no target (MM-D-150).
  const money = address.virtualAccountId !== null;
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  async function handleSave() {
    if (body.trim().length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const req: MeetingItemCreateRequest = {
        kind,
        body: body.trim().slice(0, 1000),
      };
      if (ownerUserId !== '') req.ownerUserId = ownerUserId;
      if (dueOn) req.dueOn = dueOn;
      if (kind === 'GOAL' && money) {
        if (envelopeId !== '') req.envelopeId = envelopeId;
        const minor = parseMoneyToMinor(target, currency, locale);
        if (minor !== null) req.targetMinor = minor;
      }
      await meetingsApi.createItem(
        address.virtualAccountId,
        address.meetingId,
        address.occurrenceKey,
        req,
      );
      onSaved();
    } catch {
      setError(t('meetings.errorGeneric'));
      guard.focusErrors();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId="item-new"
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 600, maxWidth: 'calc(100% - 32px)' } } }}
    >
      <DialogTitle id="item-new">{title ?? t('items.new')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('items.bodyLabel')}
            value={body}
            onChange={(event) => setBody(event.target.value.slice(0, 1000))}
            multiline
            minRows={3}
            required
            fullWidth
            sx={{
              ...dense,
              '& .MuiOutlinedInput-root': { minHeight: 88, alignItems: 'flex-start' },
            }}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
            <FormControl fullWidth sx={dense}>
              <InputLabel id="item-owner-new">{t('items.ownerLabel')}</InputLabel>
              <Select
                labelId="item-owner-new"
                value={ownerUserId === '' ? '' : String(ownerUserId)}
                displayEmpty
                onChange={(event) =>
                  setOwnerUserId(event.target.value === '' ? '' : Number(event.target.value))
                }
              >
                <MenuItem value="">{t('items.ownerNone')}</MenuItem>
                {people.map((person) => (
                  <MenuItem key={person.userId} value={String(person.userId)}>
                    {person.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('items.dueOn')}
              type="date"
              value={dueOn}
              onChange={(event) => setDueOn(event.target.value)}
              fullWidth
              sx={dense}
            />
          </Stack>
          <FormControl fullWidth sx={dense}>
            <InputLabel id="item-kind">{t('items.kindLabel')}</InputLabel>
            <Select
              labelId="item-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as ItemKind)}
            >
              {KINDS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`items.kind.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {kind === 'GOAL' && money && (
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
              <FormControl fullWidth sx={dense}>
                <InputLabel id="item-envelope">{t('items.envelopeLabel')}</InputLabel>
                <Select
                  labelId="item-envelope"
                  value={envelopeId === '' ? '' : String(envelopeId)}
                  displayEmpty
                  onChange={(event) =>
                    setEnvelopeId(event.target.value === '' ? '' : Number(event.target.value))
                  }
                >
                  <MenuItem value="">{t('items.envelopeNone')}</MenuItem>
                  {envelopes.map((envelope) => (
                    <MenuItem key={envelope.id} value={String(envelope.id)}>
                      {envelope.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('items.targetLabel')}
                value={target}
                onChange={(event) => setTarget(event.target.value)}
                fullWidth
                sx={dense}
              />
            </Stack>
          )}
          <Typography
            sx={{
              pt: 1.5,
              borderTop: `1px solid ${tokens.line}`,
              fontSize: 12,
              color: tokens.textLabel('surfaceRaised'),
            }}
          >
            {t('items.versionNoteNew')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose}>{t('common.cancel')}</Button>
        <SubmitButton
          onClick={() => void handleSave()}
          disabled={submitting || body.trim().length === 0}
          loading={submitting}
        >
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
